import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Type definitions for all platform config keys
export interface PlatformConfigValues {
  // Tenant Creation
  max_tenants_per_agency: number;
  tenant_creation_enabled: boolean;
  require_tenant_verification: boolean;
  allowed_tenant_plans: string[];
  // Branding
  branding_enabled: boolean;
  custom_domains_enabled: boolean;
  max_logo_size_kb: number;
  allowed_fonts: string[];
  require_powered_by: boolean;
  // Security
  allow_impersonation: boolean;
  impersonation_max_duration_minutes: number;
  session_timeout_minutes: number;
}

export interface ConfigMetadata {
  updated_at: string | null;
  updated_by: string | null;
}

export interface PlatformConfigItem<T = unknown> {
  value: T;
  updated_at: string | null;
  updated_by: string | null;
}

export type PlatformConfig = {
  [K in keyof PlatformConfigValues]: PlatformConfigItem<PlatformConfigValues[K]>;
};

// Default values matching the database seeds
export const defaultConfigValues: PlatformConfigValues = {
  max_tenants_per_agency: 100,
  tenant_creation_enabled: true,
  require_tenant_verification: false,
  allowed_tenant_plans: ["starter", "pro", "enterprise"],
  branding_enabled: true,
  custom_domains_enabled: true,
  max_logo_size_kb: 512,
  allowed_fonts: ["Inter", "Roboto", "Open Sans", "Poppins", "Lato"],
  require_powered_by: true,
  allow_impersonation: false,
  impersonation_max_duration_minutes: 60,
  session_timeout_minutes: 480,
};

// Parse value from API response
function parseConfigValue<K extends keyof PlatformConfigValues>(
  key: K,
  rawValue: unknown
): PlatformConfigValues[K] {
  const defaultValue = defaultConfigValues[key];
  
  if (rawValue === null || rawValue === undefined) {
    return defaultValue;
  }

  // Handle based on expected type
  if (typeof defaultValue === "boolean") {
    if (typeof rawValue === "boolean") return rawValue as PlatformConfigValues[K];
    if (typeof rawValue === "string") return (rawValue === "true") as PlatformConfigValues[K];
    return defaultValue;
  }

  if (typeof defaultValue === "number") {
    if (typeof rawValue === "number") return rawValue as PlatformConfigValues[K];
    if (typeof rawValue === "string") {
      const parsed = Number(rawValue);
      return (isNaN(parsed) ? defaultValue : parsed) as PlatformConfigValues[K];
    }
    return defaultValue;
  }

  if (Array.isArray(defaultValue)) {
    if (Array.isArray(rawValue)) return rawValue as PlatformConfigValues[K];
    if (typeof rawValue === "string") {
      try {
        const parsed = JSON.parse(rawValue);
        return Array.isArray(parsed) ? parsed as PlatformConfigValues[K] : defaultValue;
      } catch {
        return defaultValue;
      }
    }
    return defaultValue;
  }

  return rawValue as PlatformConfigValues[K];
}

// Hook to fetch all platform config
export function usePlatformConfig() {
  return useQuery({
    queryKey: ["platform-config"],
    queryFn: async (): Promise<PlatformConfig> => {
      const { data, error } = await supabase.functions.invoke("Calie_getPlatformConfig", {
        method: "GET",
      });

      if (error) {
        console.error("Failed to fetch platform config:", error);
        throw new Error(error.message || "Failed to fetch platform configuration");
      }

      const rawConfig = data?.config || {};
      
      // Build typed config object
      const config: Record<string, PlatformConfigItem<unknown>> = {};
      const keys = Object.keys(defaultConfigValues) as (keyof PlatformConfigValues)[];
      
      for (const key of keys) {
        const rawItem = rawConfig[key];
        config[key] = {
          value: parseConfigValue(key, rawItem?.value),
          updated_at: rawItem?.updated_at || null,
          updated_by: rawItem?.updated_by || null,
        };
      }

      return config as PlatformConfig;
    },
    staleTime: 1000 * 60, // 1 minute
  });
}

// Hook to update a single config key
export function useUpdatePlatformConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, value }: { key: keyof PlatformConfigValues; value: unknown }) => {
      const { data, error } = await supabase.functions.invoke("Calie_updatePlatformConfig", {
        method: "POST",
        body: { key, value },
      });

      if (error) {
        console.error("Failed to update platform config:", error);
        throw new Error(error.message || "Failed to update configuration");
      }

      return data;
    },
    onSuccess: (data, variables) => {
      // Invalidate query to refetch
      queryClient.invalidateQueries({ queryKey: ["platform-config"] });
      console.log(`Updated ${variables.key} successfully`);
    },
    onError: (error) => {
      toast.error("Failed to save configuration", {
        description: error.message,
      });
    },
  });
}

// Hook to update multiple config keys at once
export function useBatchUpdatePlatformConfig() {
  const updateConfig = useUpdatePlatformConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<PlatformConfigValues>) => {
      const entries = Object.entries(updates) as [keyof PlatformConfigValues, unknown][];
      
      // Update each config key sequentially to maintain consistency
      for (const [key, value] of entries) {
        await updateConfig.mutateAsync({ key, value });
      }
      
      return updates;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-config"] });
      toast.success("Settings saved successfully");
    },
    onError: (error) => {
      toast.error("Failed to save some settings", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    },
  });
}
